import React, { useState } from "react";
import { Link } from "react-router-dom";
import {Rating} from "@mui/lab";

// ─────────────────────────────────────────────
//  Props — Passing Data to Components
//  Chapter 2 · Components
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_0 = `// Parent passes props like HTML attributes
<UserProfile
  name="Alice"
  age={30}
  isPremium={true}
  scores={[95, 87, 92]}
  address={{ city: 'Lagos', country: 'Nigeria' }}
  onLogout={() => console.log('logout')}
/>

// Child receives them as a single 'props' object
// or via destructuring (preferred)
function UserProfile({ name, age, isPremium, scores, address, onLogout }) {
  return (
    <div>
      <h2>{name}, {age}</h2>
      {isPremium && <Badge>Premium</Badge>}
      <p>{address.city}, {address.country}</p>
      <p>Avg score: {scores.reduce((a,b) => a+b,0) / scores.length}</p>
      <button onClick={onLogout}>Logout</button>
    </div>
  );
}`;

const CODE_1 = `<MyComponent
  // String
  title="Hello World"
  // Number (curly braces for non-string values)
  count={42}
  // Boolean  — just the attribute name = true
  isActive
  isDisabled={false}
  // Array
  tags={['react', 'javascript', 'frontend']}
  // Object
  user={{ id: 1, name: 'Alice' }}
  // Function (callback)
  onClick={() => handleClick()}
  onChange={handleChange}
  // JSX
  icon={<StarIcon />}
  // Null / undefined — renders nothing
  optional={null}
/>`;

const CODE_2 = `// Parent — owns the state, passes callback down
function App() {
  const [selected, setSelected] = useState(null);

  function handleProductSelect(product) {
    setSelected(product);
    console.log('Selected:', product.name);
  }

  return (
    <div>
      <ProductList onSelect={handleProductSelect} />
      {selected && <ProductDetail product={selected} />}
    </div>
  );
}

// Child — calls parent's callback with data
function ProductList({ onSelect }) {
  const products = [
    { id: 1, name: 'Laptop', price: 999 },
    { id: 2, name: 'Phone',  price: 599 },
  ];

  return (
    <ul>
      {products.map(p => (
        <li key={p.id}>
          {p.name} — \${p.price}
          <button onClick={() => onSelect(p)}>Select</button>
        </li>
      ))}
    </ul>
  );
}`;

const CODE_3 = `const buttonProps = {
  type: 'submit',
  disabled: false,
  className: 'btn-primary',
};

// Spread — equivalent to writing each prop manually
<Button {...buttonProps} onClick={handleSubmit} />

// Forwarding all props (HOC / wrapper pattern)
function IconButton({ icon, ...rest }) {
  return (
    <button {...rest}>              {/* passes all remaining props */}
      <span>{icon}</span>
      {rest.children}
    </button>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function PropsPassingDataToComponents() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 2 · Components</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Props — Passing Data to Components</h1>
        <p style={styles.desc}>Learn how to pass and receive data between components using props.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── What are Props? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>What are Props?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Props (short for properties) are the mechanism for passing data from a <b>parent component</b> to a <b>child component</b>. They work just like function arguments. Props are <b>read-only</b> — a component should never modify its own props. This enforces React&#39;s one-way data flow principle." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_0, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_0)}</pre>
        </div>
      </div>

      /* ── All Prop Types You Can Pass ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>All Prop Types You Can Pass</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Props can hold any JavaScript value — string, number, boolean, array, object, function, even JSX." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Callback Props — Child to Parent ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Callback Props — Child to Parent</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "When a child needs to send data up to its parent, the parent passes a <b>callback function</b> as a prop. The child calls it when needed." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 2)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 2 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Spreading Props ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Spreading Props</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Use the spread operator to pass all properties of an object as individual props. Useful for forwarding props to child components." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_3, 3)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 3 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_3)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. Can a child component modify its props? Why?</div>
          <div style={styles.qItem}>✦ 2. What is the difference between props.children and a named prop?</div>
          <div style={styles.qItem}>✦ 3. How do you pass a boolean prop that defaults to true?</div>
          <div style={styles.qItem}>✦ 4. What is prop drilling and why is it a problem?</div>
            <div style={styles.qItem}>✦ 5. Write a <Rating> component that accepts stars (1-5) and readonly (bool) props.</Rating></div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
